/**
 * Action Bar
 *
 * Bottom action buttons for the media picker:
 * - Cancel (outline button)
 * - Bulk posts (split button with dropdown for Bulk Stories)
 * - Create post (filled primary button)
 *
 * All buttons are h-8 (32px) from Figma, NOT h-10.
 * Disabled state uses opacity-40.
 */

import { cn } from "lib/utils";
import { SplitButton } from "components/ui/split-button";

export type MediaPickerMode = "new-post" | "add-to-post";

export interface ActionBarProps {
  // Mode: "new-post" shows bulk posts + create post; "add-to-post" shows only "Add to post"
  mode?: MediaPickerMode;

  // Cancel
  onCancel: () => void;

  // Bulk posts (primary action of split button) — only shown in "new-post" mode
  onBulkPosts?: () => void;
  bulkPostsDisabled?: boolean;

  // Bulk stories (dropdown option of split button) — only shown in "new-post" mode
  onBulkStories?: () => void;

  // Create post / Add to post (primary CTA)
  onCreatePost: () => void;
  createPostDisabled?: boolean;

  // Selected count
  selectedCount?: number;
  maxCount?: number;

  className?: string;
}

export function ActionBar({
  mode = "new-post",
  onCancel,
  onBulkPosts,
  bulkPostsDisabled = false,
  onBulkStories,
  onCreatePost,
  createPostDisabled = false,
  selectedCount = 0,
  maxCount = 20,
  className,
}: ActionBarProps) {
  const isAddToPost = mode === "add-to-post";

  return (
    <div
      className={cn(
        "flex items-center justify-between",
        "px-6 py-4",
        "border-t border-neutral-100 bg-white",
        className
      )}
    >
      {/* Left side - Cancel button */}
      <button
        type="button"
        onClick={onCancel}
        className={cn(
          // Figma: h-8 (32px), NOT h-10
          "h-8 px-3 py-[6px]",
          "border border-neutral-100 rounded-lg bg-white",
          "shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
          "font-inter text-sm font-medium text-neutral-700",
          "transition-colors",
          "hover:bg-neutral-50 hover:border-neutral-300",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        )}
      >
        Cancel
      </button>

      {/* Right side - Bulk and Create buttons */}
      <div className="flex items-center gap-3">
        {/* Selected count indicator */}
        {selectedCount > 0 && (
          <p className="text-sm text-neutral-500 font-inter">
            Selected {selectedCount}/{maxCount}
          </p>
        )}

        {/* Bulk posts split button — hidden in add-to-post mode */}
        {!isAddToPost && onBulkPosts && (
          <SplitButton
            variant="outline"
            size="sm"
            disabled={bulkPostsDisabled}
            leftLabel="Bulk posts"
            onLeftClick={onBulkPosts}
            menuTriggerAriaLabel="More bulk options"
            menuItems={
              onBulkStories
                ? [
                    {
                      key: "bulk-stories",
                      label: "Bulk Stories",
                      onSelect: onBulkStories,
                    },
                  ]
                : []
            }
          />
        )}

        {/* Create post button (primary) */}
        <button
          type="button"
          onClick={onCreatePost}
          disabled={createPostDisabled}
          className={cn(
            // Figma: h-8 (32px), NOT h-10
            "h-8 px-3 py-[6px]",
            "rounded-lg bg-blue-500",
            "shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]",
            "font-inter text-sm font-medium text-white",
            "transition-colors",
            "hover:bg-blue-600 active:bg-blue-700",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
            // Disabled state - opacity 40%
            createPostDisabled &&
              "opacity-40 cursor-not-allowed hover:bg-blue-500"
          )}
        >
          {isAddToPost ? "Add to post" : "Create post"}
        </button>
      </div>
    </div>
  );
}
